import React, { useState } from 'react';
import { ArrowLeft, BookMarked, Lightbulb, Target, CreditCard, RotateCcw, Clock, TrendingUp, Shuffle, Plus, Minus } from 'lucide-react';
import { useVocabulary } from '../context/VocabularyContext';
import { srsManager } from '../utils/srsManager';

export function WordPoolsGrid() {
  const { state, dispatch, actions } = useVocabulary();
  const { selectedCategory, vocabularyData } = state;
  const [resetConfirm, setResetConfirm] = useState(null);
  const [randomSessionCount, setRandomSessionCount] = useState(20);

  const handleBackToCategories = () => {
    dispatch({ type: actions.SET_CURRENT_VIEW, payload: 'categories' });
  };

  const handleResetSRS = (poolCode) => {
    setResetConfirm(poolCode);
  };

  const confirmResetSRS = (poolCode) => {
    const success = srsManager.resetData('words', poolCode);
    if (success) {
      alert(`Dane SRS dla puli ${poolCode} zostały zresetowane! Możesz teraz ćwiczyć wszystkie słowa z tej puli od nowa.`);
    } else {
      alert('Wystąpił błąd podczas resetowania danych SRS.');
    }
    setResetConfirm(null);
  };

  const cancelReset = () => {
    setResetConfirm(null);
  };

  const handleStartRandomSession = () => {
    try {
      // Collect all available words from all pools in this category
      const category = vocabularyData?.grammar_categories?.[selectedCategory];
      if (!category || !category.pools) {
        throw new Error('No pools found in this category');
      }

      let allAvailableWords = [];
      
      // Iterate through all pools in the category
      category.pools.forEach(poolCode => {
        const poolData = vocabularyData.pools[poolCode];
        if (poolData && poolData.words) {
          const availableWords = srsManager.getAvailableSentencesForPool('words', poolCode, poolData.words);
          // Extract actual word data from SRS wrapper and add poolCode for tracking
          const wordsWithPool = availableWords.map(srsWord => ({ 
            ...srsWord.item,  // Extract the actual word from the SRS wrapper
            id: srsWord.id,  // Keep the original index in the pool
            _poolCode: poolCode,
            _srsData: srsWord.srsData,  // Keep SRS data if needed
            _type: srsWord.type  // Keep type (new/review)
          }));
          allAvailableWords = allAvailableWords.concat(wordsWithPool);
        }
      });

      if (allAvailableWords.length === 0) {
        alert(`🎉 Gratulacje!\n\nWszystkie słowa z tej kategorii są aktualnie opanowane.`);
        return;
      }

      // Limit to requested count
      const sessionSize = Math.min(randomSessionCount, allAvailableWords.length);
      
      // Shuffle and take requested amount
      const shuffled = [...allAvailableWords].sort(() => Math.random() - 0.5);
      const selectedWords = shuffled.slice(0, sessionSize);

      console.log(`Starting random session with ${selectedWords.length} words from ${allAvailableWords.length} available`);

      // Start session
      dispatch({ 
        type: actions.START_RANDOM_WORD_SESSION, 
        payload: { 
          categoryKey: selectedCategory,
          words: selectedWords,
          mode: 'flashcards'
        } 
      });
    } catch (error) {
      console.error('Error starting random session:', error);
      alert(`Błąd podczas uruchamiania losowej sesji: ${error.message}`);
    }
  };

  const adjustRandomCount = (delta) => {
    setRandomSessionCount(prev => Math.max(10, Math.min(100, prev + delta)));
  };

  const handleStartWordExercise = async (poolCode) => {
    try {
      console.log(`Starting words exercise for pool: ${poolCode}`);
      
      // Get pool data
      const poolData = vocabularyData.pools[poolCode];
      if (!poolData || !poolData.words) {
        throw new Error(`No words found in pool ${poolCode}`);
      }
      
      const words = poolData.words;
      
      // Initialize SRS records for all items in the pool (if not already initialized)
      srsManager.initializePoolItems('words', poolCode, words);
      
      // Check if there are any words available for review
      const availableWords = srsManager.getAvailableSentencesForPool('words', poolCode, words);
      
      if (availableWords.length === 0) {
        alert(`🎉 Gratulacje!\n\nWszystkie słowa z tej puli są aktualnie opanowane.\nSprawdź zakładkę "Na jutro" lub "Opanowane" w statystykach.`);
        return;
      }
      
      console.log(`Loaded ${words.length} words, ${availableWords.length} available for review`);
      
      // Start session with available words only (always flashcards mode)
      // Pass full SRS objects with id and item for proper tracking
      dispatch({ 
        type: actions.START_WORD_SESSION, 
        payload: { 
          categoryKey: selectedCategory,
          poolCode: poolCode,
          words: availableWords,  // Pass full SRS objects (with id, item, srsData)
          mode: 'flashcards'
        } 
      });
    } catch (error) {
      console.error(`Error starting word exercise for pool ${poolCode}:`, error);
      alert(`Błąd podczas uruchamiania ćwiczenia: ${error.message}`);
    }
  };

  const getCategoryName = (categoryKey) => {
    const category = vocabularyData?.grammar_categories?.[categoryKey];
    return category?.name || `Kategoria ${categoryKey}`;
  };

  const getPoolIcon = (poolNumber) => {
    const icons = {
      '01': BookMarked,
      '02': Lightbulb, 
      '03': Target
    };
    return icons[poolNumber] || BookMarked;
  };

  // Get pools for selected category
  const getPoolsForCategory = (categoryKey) => {
    if (!vocabularyData || !vocabularyData.grammar_categories || !vocabularyData.pools) {
      return [];
    }
    
    const category = vocabularyData.grammar_categories[categoryKey];
    const pools = vocabularyData.pools;
    
    if (!category || !category.pools) {
      return [];
    }

    return category.pools.map(poolCode => {
      const poolData = pools[poolCode];
      if (!poolData) return null;
      
      const wordCount = poolData.metadata?.word_count || poolData.words?.length || 0;
      
      return {
        poolCode,
        name: poolData.name,
        description: poolData.description,
        difficulty: poolData.difficulty || poolData.metadata?.difficulty_level || 'Średni',
        wordCount: wordCount,
        color: getPoolColor(poolCode)
      };
    }).filter(Boolean);
  };

  const getPoolColor = (poolCode) => {
    // Assign colors based on pool number
    const poolNumber = poolCode.split('-')[2] || poolCode.split('-')[1];
    const colors = {
      '01': 'blue',
      '02': 'green',
      '03': 'orange',
      '04': 'purple'
    };
    return colors[poolNumber] || 'blue';
  };

  const pools = getPoolsForCategory(selectedCategory);

  // Color classes for pool cards
  const colorClasses = {
    'blue': 'bg-blue-50 border-blue-200',
    'green': 'bg-green-50 border-green-200',
    'orange': 'bg-orange-50 border-orange-200',
    'purple': 'bg-purple-50 border-purple-200'
  };

  const iconColorClasses = {
    'blue': 'bg-blue-600',
    'green': 'bg-green-600',
    'orange': 'bg-orange-600',
    'purple': 'bg-purple-600'
  };

  const difficultyColors = {
    'Łatwy': 'bg-green-100 text-green-700',
    'Średni': 'bg-yellow-100 text-yellow-700',
    'Trudny': 'bg-red-100 text-red-700'
  };

  return (
    <div className="space-y-6">
      {/* Navigation Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={handleBackToCategories}
          className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          title="Powrót do kategorii"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            {getCategoryName(selectedCategory)}
          </h1>
          <p className="text-gray-600">
            Wybierz pulę słów do nauki
          </p>
        </div>
      </div>

      {/* Random Session Section */}
      {(() => {
        // Calculate total available words across all pools
        const category = vocabularyData?.grammar_categories?.[selectedCategory];
        let totalAvailable = 0;
        
        if (category && category.pools) {
          category.pools.forEach(poolCode => {
            const poolData = vocabularyData.pools[poolCode];
            if (poolData && poolData.words) {
              const available = srsManager.getAvailableSentencesForPool('words', poolCode, poolData.words);
              totalAvailable += available.length;
            }
          });
        }

        if (totalAvailable === 0) return null;

        return (
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-indigo-600 text-white rounded-lg">
                  <Shuffle size={18} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-800">Losowa sesja z całej kategorii</h3>
                  <p className="text-xs text-gray-600">
                    Dostępnych: <span className="font-semibold text-indigo-600">{totalAvailable}</span>
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {/* Count Adjuster */}
                <div className="flex items-center gap-1 bg-white rounded-lg p-1 border border-indigo-300">
                  <button
                    onClick={() => adjustRandomCount(-10)}
                    className="p-1.5 hover:bg-indigo-100 rounded transition-colors"
                    title="Zmniejsz o 10"
                  >
                    <Minus size={16} className="text-indigo-600" />
                  </button>
                  
                  <div className="px-3 text-center min-w-[50px]">
                    <div className="text-xl font-bold text-indigo-600">{randomSessionCount}</div>
                  </div>
                  
                  <button
                    onClick={() => adjustRandomCount(10)}
                    className="p-1.5 hover:bg-indigo-100 rounded transition-colors"
                    title="Zwiększ o 10"
                  >
                    <Plus size={16} className="text-indigo-600" />
                  </button>
                </div>

                {/* Start Button */}
                <button
                  onClick={handleStartRandomSession}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 flex items-center gap-2 text-sm"
                >
                  <Shuffle size={16} />
                  Rozpocznij
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Pools Grid */}
      <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-6">
        {pools.map((pool, index) => {
          const poolNumber = String(index + 1).padStart(2, '0');
          const IconComponent = getPoolIcon(poolNumber);

          return (
            <div
              key={pool.poolCode}
              className={`p-6 ${colorClasses[pool.color]} border-2 rounded-xl transition-all duration-200`}
            >
              {/* Pool Header */}
              <div className="flex items-center mb-4">
                <div className={`p-3 ${iconColorClasses[pool.color]} text-white rounded-lg`}>
                  <IconComponent size={24} />
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-bold text-gray-800">
                    {pool.name}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {pool.poolCode}
                  </p>
                </div>
              </div>
              
              {/* Pool Description */}
              <p className="text-gray-600 mb-4">
                {pool.description}
              </p>
              
              {/* Pool Stats */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600">
                    📝 {pool.wordCount} słów
                  </span>
                  <span className={`text-xs px-2 py-1 rounded-full ${difficultyColors[pool.difficulty]}`}>
                    {pool.difficulty}
                  </span>
                </div>
              </div>

              {/* Pool SRS Stats */}
              <div className="border-t pt-3 mb-3">
                {(() => {
                  const srsStats = srsManager.getPoolStats('words', pool.poolCode, pool.wordCount);
                  return (
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1 text-orange-600">
                          <Clock size={12} />
                          <span className="font-medium">{srsStats.dueForReview}</span>
                        </div>
                        <div className="text-gray-500 text-xs">Do powtórki</div>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1 text-blue-600">
                          <Target size={12} />
                          <span className="font-medium">{srsStats.tomorrow}</span>
                        </div>
                        <div className="text-gray-500 text-xs">Na jutro</div>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1 text-green-600">
                          <TrendingUp size={12} />
                          <span className="font-medium">{srsStats.mastered}</span>
                        </div>
                        <div className="text-gray-500 text-xs">Opanowane</div>
                      </div>
                    </div>
                  );
                })()}
              </div>
              
              {/* Start Button */}
              {(() => {
                const stats = srsManager.getPoolStats('words', pool.poolCode, pool.wordCount);
                const hasAvailable = stats.dueForReview > 0;
                
                return (
                  <button
                    onClick={() => handleStartWordExercise(pool.poolCode)}
                    disabled={!hasAvailable}
                    className={`w-full font-medium py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center group mb-2 ${
                      hasAvailable
                        ? 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                    title={!hasAvailable ? 'Brak słów do powtórki' : 'Rozpocznij naukę'}
                  >
                    <CreditCard size={18} className="mr-2 group-hover:scale-110 transition-transform" />
                    {hasAvailable ? 'Rozpocznij fiszki' : '✓ Wszystko opanowane'}
                  </button>
                );
              })()}
              
              {/* Reset SRS Button */}
              <button
                onClick={() => handleResetSRS(pool.poolCode)}
                className="w-full bg-orange-100 hover:bg-orange-200 text-orange-700 font-medium py-2 px-4 rounded-lg transition-all duration-200 flex items-center justify-center group text-sm"
                title="Resetuj postępy SRS dla tej puli"
              >
                <RotateCcw size={16} className="mr-2 group-hover:rotate-180 transition-transform" />
                Resetuj postępy
              </button>
            </div>
          );
        })}
      </div>

      {/* No pools message */}
      {pools.length === 0 && (
        <div className="text-center py-8">
          <div className="text-gray-500">
            <span className="text-4xl block mb-2">📚</span>
            Brak dostępnych pul dla tej kategorii
          </div>
        </div>
      )}

      {/* Reset Confirmation Dialog */}
      {resetConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4 text-orange-600">⚠️ Resetuj postępy SRS</h3>
            <p className="mb-6 text-gray-700">
              Czy na pewno chcesz zresetować postępy SRS dla puli <strong>{resetConfirm}</strong>?
              <br /><br />
              <span className="text-orange-600">Ta akcja jest nieodwracalna!</span> Wszystkie słowa z tej puli będą ponownie dostępne do nauki.
            </p>
            <div className="flex gap-3">
              <button 
                onClick={() => confirmResetSRS(resetConfirm)}
                className="flex-1 py-2 px-4 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
              >
                🔄 Resetuj
              </button>
              <button 
                onClick={cancelReset}
                className="flex-1 py-2 px-4 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 